import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";

let _planCache = null;
let _planPromise = null;
let _planFetchedAt = 0;
const PLAN_TTL_MS = 60_000;

export function refreshPlanCache() {
  _planCache = null;
  _planPromise = null;
  _planFetchedAt = 0;
}

export function usePlan() {
  const [plan, setPlan] = useState(_planCache);
  useEffect(() => {
    const load = (force) => {
      const fresh = !force && _planCache && Date.now() - _planFetchedAt < PLAN_TTL_MS;
      if (fresh) return;
      if (force) refreshPlanCache();
      if (!_planPromise) {
        _planPromise = api.get("/payments/plan").then((r) => {
          _planCache = r.data;
          _planFetchedAt = Date.now();
          return r.data;
        }).catch(() => _planCache || { plan: "pro" }) // fail open: never block UI on errors
          .finally(() => { _planPromise = null; });
      }
      _planPromise.then((d) => setPlan(d));
    };
    load(false);
    // Fired by the store billing layer once a purchase got verified server-side.
    const onChanged = () => load(true);
    window.addEventListener("solano:plan-changed", onChanged);
    return () => window.removeEventListener("solano:plan-changed", onChanged);
  }, []);
  return plan;
}

/** Small golden "PRO" chip shown next to AI features for Basic users only.
 *  Tapping it opens the subscription page — the subtle conversion path.
 *  When the free quota is known, the remaining count is shown alongside. */
export function ProBadge({ className = "", showQuota = false }) {
  const plan = usePlan();
  const navigate = useNavigate();
  const { t } = useT();
  if (!plan || plan.plan === "pro") return null;
  const left = plan.ai_limit != null ? Math.max(0, plan.ai_limit - (plan.ai_used || 0)) : null;
  return (
    <span
      data-testid="pro-badge"
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate("/app/abo"); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate("/app/abo"); } }}
      className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#FC9700] to-[#d97f00] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 shadow-sm cursor-pointer active:scale-95 transition ${className}`}
    >
      <img src="https://customer-assets-agu9un31.emergentagent.net/job_nutrifit-client-1/artifacts/1xx7d8jc_IMG_0496.webp" alt="" className="h-2.5 w-2.5 object-contain brightness-0 invert" /> Pro
      {showQuota && left != null && (
        <span className="ml-0.5 font-semibold normal-case opacity-90">· {left} {t("profile.proQuotaLeft") || "frei"}</span>
      )}
    </span>
  );
}
